<template>
    <v-container fluid class="pa-0 pa-md-3">
        <v-row :no-gutters="$vuetify.breakpoint.mdAndDown">
            <v-col cols="12">
                <v-card class="elevation-0">
                    <v-container fluid class="ma-0 pa-0 q_leads_1" :id="entity + '_datatable'">
                        <QDataTableHeader color="q_leads_1" ratio="5/7">
                            <template #left>
                                <div id="q-pending-policy-step-3">
                                    <div class="d-flex align-center">
                                        <p class="q-display-3 pb-0 pt-0 mt-0 mb-0 white--text">{{ title }}</p>
                                        <v-switch
                                            v-if="hasAgencyOwnerPermissions"
                                            v-model="baseShopOnly"
                                            :label="baseShopOnly ? 'Base Shop' : 'Personal Production'"
                                            hide-details
                                            inset
                                            dark
                                            class="ml-8 mt-0 pt-0"
                                            @change="refreshData(true)"
                                        />
                                    </div>
                                </div>
                            </template>
                            <template #right>
                                <v-spacer />
                                <v-text-field
                                    v-model="search"
                                    dense
                                    dark
                                    append-icon="icon-q-search"
                                    label="Client Name or Policy #"
                                    single-line
                                    hide-details
                                    @keyup="maybeSearch"
                                    @click:append="refreshData()"
                                />
                            </template>
                        </QDataTableHeader>
                    </v-container>
                    <v-row justify="space-between" class="my-3 mx-8">
                        <v-btn text small color="q_leads_4" @click="toggleFilters()">
                            <v-icon small left>fas fa-filter</v-icon> Filters
                        </v-btn>
                        <v-btn v-if="responseData.length > 0" text small color="q_leads_4" @click="exportResults">
                            <v-icon small left>fas fa-file-export</v-icon> Export Results
                        </v-btn>
                    </v-row>
                    <v-row v-show="showFilters" justify="center">
                        <QFilters
                            v-model="filters"
                            :keys="filter_keys"
                            :available-filters="available_filters"
                            :filters-details="filtersDetails"
                            :hide-button="true"
                            dark
                            alignment="center"
                            ref="filters"
                        />
                    </v-row>
                    <QExpandableDataTable
                        :items-per-page.sync="rows"
                        :search="search"
                        :headers="allHeaders"
                        :visibleHeaders="visibleHeaders"
                        :items="all_data"
                        :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
                        :loading="isLoading"
                        :options.sync="options"
                        :server-items-length="totalServerItems"
                        item-key="ID"
                        color="q_leads_1"
                        class="elevation-0"
                        fluid
						:allow-expand-key="allowExpandKey"
                    >
                        <template #item.AgentName="{ item }">
                            <router-link v-if="item.AgentCode" class="q_leads_4--text" :to="'/agents/' + item.AgentCode">{{ item.AgentName }}</router-link>
                            <span v-else>{{ item.AgentName }}</span>
                        </template>
                        <template #item.PolicyStatus="{ item }">
                            <div class="flex items-center gap-2">
                                <!-- <a @click.stop="showStatusModal(item)">{{ item.PolicyStatus }}</a> -->
                                <span>{{ item.PolicyStatus }}</span>
                                <v-badge v-if="item.IncompletePolicyRequirementsCount" :content="item.IncompletePolicyRequirementsCount" color="red" inline />
                            </div>
                        </template>
                        <template #expand="{ item }">
                            <v-simple-table>
                                <thead>
                                    <tr class="font-weight-bold">
                                        <td>Requested Date</td>
                                        <td>Received Date</td>
                                        <td>Requirements</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="requirement in item.PolicyRequirements" :key="requirement.id">
                                        <td>{{ requirement.RequestedDate }}</td>
                                        <td>
                                            <span v-if="requirement.RequirementComplete === REQUIREMENT_STATUS.COMPLETE && requirement.ReceivedDate">{{ requirement.ReceivedDate }}</span>
                                        </td>
                                        <td>
                                            <v-row align="center">
                                                <v-icon v-if="requirement.RequirementComplete === REQUIREMENT_STATUS.COMPLETE" color="green" small left>fas fa-check</v-icon>
                                                <v-badge v-else content=" " color="red" inline />
                                                <span>{{ requirement.Description }}</span>
                                            </v-row>
                                        </td>
                                    </tr>
                                    <tr v-if="!item.PolicyRequirements || !item.PolicyRequirements.length">
                                        <td class="px-4">No data.</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </template>
                    </QExpandableDataTable>
                </v-card>
            </v-col>
        </v-row>
        <v-dialog v-if="selectedStatusItem" v-model="dialog" width="400">
            <v-card class="pa-6 text-center" min-height="">
                <v-card-title class="justify-center">
                    <h4>{{ selectedStatusItem.PolicyStatus }}</h4>
                </v-card-title>
                <p>Lorem ipsum ...</p>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { debounce } from 'vue-debounce'
import QuilityAPI from '@/store/API/QuilityAPI.js'
import QDataTableMixin from "./QDataTableMixin.js"
import QDataTableHeader from './base/QDataTableHeader.vue'
import QExpandableDataTable from './QExpandableDataTable.vue'

const allHeaders = [
    {
        text: 'Submitted Date',
        value: 'SubmittedDate',
        align: 'start',
        sortable: true,
        filterable: true
    },
    {
        text: 'Agent',
        value: 'AgentName',
        align: 'start',
        sortable: false,
        filterable: true
    },
    {
        text: 'Client',
        value: 'InsuredName',
        align: 'start',
        sortable: true,
        filterable: true,
        class: 'header-class',
    },
    {
        text: 'Face Amount',
        value: 'FaceAmount',
        align: 'start',
        sortable: true,
        filterable: true
    },
    {
        text: 'APV',
        value: 'AnnualPremium',
        align: 'start',
        sortable: true,
        filterable: true
    },
    {
        text: 'Carrier',
        value: 'OrigCarrier',
        align: 'start',
        sortable: true,
        filterable: true,
        class: 'header-class',
    },
    {
        text: 'Product',
        value: 'Product',
        align: 'start',
        sortable: true,
        filterable: true
    },
    {
        text: 'Status',
        value: 'PolicyStatus',
        align: 'start',
        sortable: true,
        filterable: true,
    },
    {
        text: 'Policy #',
        value: 'PolicyNumber',
        align: 'start',
        sortable: true,
        filterable: true
    },
]

const REQUIREMENT_STATUS = {
    INCOMPLETE: '0',
    COMPLETE: '1',
}

const ALL_OPTION = '-All-'

export default {
    //override this.
    name: "QPendingPolicyDataTable",
    mixins: [QDataTableMixin],
    props: ['agent', 'for_baseshop'],
    data: function() {
        if (this.for_baseshop){
            var bsOnly = this.for_baseshop
        }else{
            if (this.hasRole(['Staff', 'SuperAdmin', 'Exec']) && this.agent.AgentCode == 'SFG0000001'){
                bsOnly = true;
            }else{
                bsOnly = false;
            }
        }
        
        return {
            dataLoading: false,
            entity: 'pendingpolicy',
            isExporting: false,
            //use this to "preset" a filter
            //filters: { 'Status': 'Active' },
            firstPull: true, // this keeps the auto scrolling on data reloads from happening on the first load.
            responseData: [],
            totalServerItems: 0,
            filtersOptions: null,
            options: { sortBy: ['SubmittedDate'], sortDesc: ["true"] },
            selectedStatusItem: null,
            allHeaders,
            REQUIREMENT_STATUS,
            allowExpandKey: 'expandable',
            baseShopOnly: bsOnly,
        }
    },
    created: function() {
        this.refreshData();
    },
    computed: {
        filter_keys: function() {
            // these should match up with the keys for the model on the server side - availableFilters
            const keys = ["Carrier", "PolicyStatus"]

            const hasDownlineAgents = this.filtersOptions?.Agents && this.filtersOptions.Agents.some(item => item !== ALL_OPTION && item.value !== this.agent.AgentCode)

            return this.baseShopOnly && hasDownlineAgents
                ? [...keys, 'Agents']
                : keys
        },
        all_data: function() {
            //instead of using a state/model on the frontend the data is all contained in this module.
            //this computed propery is required by the QDataTableMixin
            return this.responseData.map((item) => {
                const incompletePolicyRequirements = item.PolicyRequirements
                    ? item.PolicyRequirements.filter(({ RequirementComplete }) => RequirementComplete === REQUIREMENT_STATUS.INCOMPLETE)
                    : []

                return {
                    ...item,
                    FaceAmount: item.FaceAmount ? this.formatCurrency(item.FaceAmount) : 'NA',
                    AnnualPremium: item.AnnualPremium ? this.formatCurrency(item.AnnualPremium) : 'NA',
                    SubmittedDate: item.SubmittedDate ? this.formatDate(item.SubmittedDate) : 'NA',
                    OrigCarrier: item.Carrier,
                    IncompletePolicyRequirementsCount: incompletePolicyRequirements.length,
					[this.allowExpandKey]: !!item.PolicyRequirements?.length,
                }
            })
        },
        visibleHeaders() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return allHeaders.slice(0, 2)
            }

            if (this.$vuetify.breakpoint.mdAndDown) {
                return allHeaders.slice(0, 5)
            }

            return allHeaders.slice(0, 9)
        },
        carrierSelected() {
            return this.filters?.Carrier && this.filters?.Carrier !== ALL_OPTION
        },
        // filtersOptions will be set by our first data pull where the server responds with all data for the filter drop downs.
        // QDataTableMixin requires this computed property.
        available_filters() {
            if (!this.filtersOptions) {
                return this.filtersOptions
            }

            const agentsFilter = this.filtersOptions.Agents.filter((item) => typeof item !== 'object' || item.value !== this.agent.AgentCode)
            const filtersOptions = {
                ...this.filtersOptions,
                Agents: agentsFilter,
            }

            if (!this.carrierSelected) {
                return filtersOptions
            }

            const carrier = this.filtersOptions.Carrier.find(item => item.value === this.filters?.Carrier)
            return {
                ...filtersOptions,
                PolicyStatus: [ALL_OPTION, ...(carrier?.statuses || [])],
            }
        },
        isLoading: function() {
            return this.dataLoading
        },
        filtersDetails() {
            return this.carrierSelected
                ? []
                : [
                    {
                        key: "PolicyStatus",
                        details: {
                            disabled: true,
                            "error-messages": "You must choose a carrier before you can select a status",
                        },
                    },
                ]
        },
        hasAgencyOwnerPermissions() {
            return this.user.Agent.IsAgencyOwner || this.user.Agent.HasManagerAccess
        },
    },
    methods: {
        //get called by the QDataTableMixin whenever we need to refresh the data from the server.
        loadData: debounce(async function(options) {
            const opt = { ...options }
            this.dataLoading = true;

            // if the prop for_baseshop
            // is set, then that means
            // that we are using this
            // compnent to expose the
            // policy data for the agents
            // in the baseshop of this
            // agent. This currently
            // happens when we access
            // this component from the
            // baseshop_pending_policy
            // view

            if (this.isExporting) {
                opt.itemsPerPage = "csv"
            }

            if (this.baseShopOnly) {
                opt.baseshop = true
            } else {
                opt.personal_only = true
            }

            const agentCode = this.filters?.Agents && this.filters?.Agents !== ALL_OPTION
                ? this.filters.Agents
                : this.agent.AgentCode
            // set component fields to work with agent policies
            await QuilityAPI.getAgentPendingPolicies(agentCode, this.filters, opt)
                .then((json) => {
                    if (this.isExporting) {
                        this.exportCSVFile('pending-policy-data.csv', json);
                    } else {
                        this.setResponseData(json)
                    }
                })

            this.dataLoading = false
            this.firstPull = false
        }, 200),
        exportResults() {
            this.isExporting = true
            this.refreshData();
        },
        exportCSVFile(name, data) {
            const fileURL = window.URL.createObjectURL(new Blob([data]));
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', name);
            document.body.appendChild(fileLink);
            fileLink.click();
            document.body.removeChild(fileLink);
            this.isExporting = false;
        },
        setResponseData(response) {
            //all the good data returned
            this.responseData = response.data
            //lets the data table know how many results the full set is from the server.
            this.totalServerItems = response.meta.total
            if (typeof response.meta != 'undefined' && typeof response.meta.filters != 'undefined') {
                this.filtersOptions = response.meta.filters
            }
        },
        showStatusModal(item) {
            this.selectedStatusItem = item
            this.dialog = true
        },
    },
    watch: {
        'agent.AgentCode': function() {
            this.$nextTick(this.refreshData) // this is the QDataTableMixin call that does stuff needed before actually calling loadData above.
        },
        'filters.Carrier'() {
            this.filters.PolicyStatus = ALL_OPTION
        },
    },
    components: {
        QDataTableHeader,
        QExpandableDataTable,
    },
}
</script>

<style scoped>
.nowrap {
    white-space: nowrap;
}
</style>
